import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import Login from '@/pages/Login';
import Home from '@/pages/Home';
import Billing from '@/pages/Billing';
import PasteWhatsappSales from '@/pages/PasteWhatsappSales';
import WaImportSettings from '@/pages/WaImportSettings';
import NeedsReview from '@/pages/NeedsReview';
import InventoryReview from '@/pages/InventoryReview';
import InvoiceView from '@/pages/InvoiceView';
import InvoiceEdit from '@/pages/InvoiceEdit';
import Stock from '@/pages/Stock';
import StockAdjustment from '@/pages/StockAdjustment';
import GarmentDetail from '@/pages/GarmentDetail';
import AddStock from '@/pages/AddStock';
import PurchaseWizard from '@/pages/PurchaseWizard';
import Transfers from '@/pages/Transfers';
import ReturnsExchanges from '@/pages/ReturnsExchanges';
import Customers from '@/pages/Customers';
import Suppliers from '@/pages/Suppliers';
import Purchases from '@/pages/Purchases';
import PurchaseDetail from '@/pages/PurchaseDetail';
import Expenses from '@/pages/Expenses';
import Reports from '@/pages/Reports';
import ProfitReport from '@/pages/ProfitReport';
import StockReport from '@/pages/StockReport';
import SalesHistory from '@/pages/SalesHistory';
import DailyClosing from '@/pages/DailyClosing';
import More from '@/pages/More';
import Settings from '@/pages/Settings';
import Admin from '@/pages/Admin';
import Audit from '@/pages/Audit';
import DataManagement from '@/pages/DataManagement';
import StaffPage from '@/pages/StaffPage';
import DueReminders from '@/pages/DueReminders';
import AccountsBreakup from '@/pages/AccountsBreakup';
import Integrations from '@/pages/Integrations';
import SystemHealth from '@/pages/SystemHealth';
import ManagementDashboard from '@/pages/ManagementDashboard';
import PartnerHome from '@/pages/PartnerHome';
import OwnerOverride from '@/pages/OwnerOverride';
import { Loader2 } from 'lucide-react';
import { SplashGate } from '@/components/SplashScreen';
import { NotificationsGate } from '@/components/NotificationsGate';
import { InstallPrompt } from '@/components/InstallPrompt';
import { OfflineBanner } from '@/components/OfflineBanner';
import { UpdateBanner } from '@/components/UpdateBanner';
import { ForcePasswordChange } from '@/components/ForcePasswordChange';
import { BiometricEnrollGate } from '@/components/BiometricEnrollGate';

const homeAllowed = (user, can) => !!(user && (user.role === 'OWNER' || user.owner_access || user.role === 'MANAGER' || (can && can('dashboard'))));

// The first page a user is actually allowed to see (used for landing redirects).
const firstAllowedPath = (user, can) => {
  if (homeAllowed(user, can)) return '/';
  const order = [
    ['billing_view', '/billing'], ['transfer_view', '/transfers'], ['stock_view', '/stock'],
    ['returns', '/returns'], ['exchanges', '/returns'], ['customers_view', '/customers'],
    ['purchases_view', '/purchases'], ['suppliers_view', '/suppliers'], ['expenses_view', '/expenses'],
    ['settings_view', '/settings'],
  ];
  for (const [perm, path] of order) if (can && can(perm)) return path;
  return '/more';
};

// Landing: show the dashboard only to users allowed to see it; otherwise send
// them straight to the first page their permissions allow.
const LandingGate = () => {
  const { user, can } = useApp();
  if (homeAllowed(user, can)) return <Home />;
  return <Navigate to={firstAllowedPath(user, can)} replace />;
};

// Mounts the biometric enrollment prompt and the forced password-change screen.
// Both are no-ops until a user is signed in.
const AuthGates = () => {
  const { user, booting } = useApp();
  if (booting || !user) return null;
  if (user.must_change_password) return <ForcePasswordChange />;
  return <BiometricEnrollGate />;
};

const RequireAuth = ({ children, perm, ownerOnly }) => {
  const { user, booting, can } = useApp();
  const location = useLocation();
  if (booting) return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Sleeping partners can ONLY reach the internal Management Dashboard (read-only).
  if (user.role === 'PARTNER' && location.pathname !== '/management') {
    return <Navigate to="/management" replace />;
  }
  const owner = user.role === 'OWNER' || user.owner_access;
  // Owner-only areas (e.g. Reports) are never shown to anyone else.
  if (ownerOnly && !owner) return <Navigate to={firstAllowedPath(user, can)} replace />;
  // Enforce permissions on direct navigation: a user only ever reaches pages
  // their permissions allow — anything else sends them to their landing page.
  if (perm) {
    const perms = Array.isArray(perm) ? perm : [perm];
    if (!owner && !perms.some((p) => can && can(p))) {
      return <Navigate to={firstAllowedPath(user, can)} replace />;
    }
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <SplashGate />
      <AuthGates />
      <BrowserRouter>
        <NotificationsGate />
        <InstallPrompt />
        <UpdateBanner />
        <OfflineBanner />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/owner-override" element={<OwnerOverride />} />
          <Route path="/" element={<RequireAuth><LandingGate /></RequireAuth>} />
          <Route path="/billing" element={<RequireAuth perm={['billing_view', 'billing_create']}><Billing /></RequireAuth>} />
          <Route path="/paste-whatsapp-sales" element={<RequireAuth perm="billing_create"><PasteWhatsappSales /></RequireAuth>} />
          <Route path="/wa-import-settings" element={<RequireAuth perm="billing_create"><WaImportSettings /></RequireAuth>} />
          <Route path="/needs-review" element={<RequireAuth perm="stock_edit"><NeedsReview /></RequireAuth>} />
          <Route path="/inventory-review" element={<RequireAuth perm={['stock_edit', 'reports_view']}><InventoryReview /></RequireAuth>} />
          <Route path="/invoice/:invoiceNo" element={<RequireAuth><InvoiceView /></RequireAuth>} />
          <Route path="/invoice/:invoiceNo/edit" element={<RequireAuth perm="billing_edit"><InvoiceEdit /></RequireAuth>} />
          <Route path="/stock" element={<RequireAuth perm="stock_view"><Stock /></RequireAuth>} />
          <Route path="/stock-adjustment" element={<RequireAuth perm="stock_adjust"><StockAdjustment /></RequireAuth>} />
          <Route path="/garment/:barcode" element={<RequireAuth perm="stock_view"><GarmentDetail /></RequireAuth>} />
          <Route path="/add-stock" element={<RequireAuth perm="stock_add"><AddStock /></RequireAuth>} />
          <Route path="/purchase" element={<RequireAuth perm="purchases_create"><PurchaseWizard /></RequireAuth>} />
          <Route path="/transfers" element={<RequireAuth perm="transfer_view"><Transfers /></RequireAuth>} />
          <Route path="/returns" element={<RequireAuth perm={['returns', 'exchanges']}><ReturnsExchanges /></RequireAuth>} />
          <Route path="/customers" element={<RequireAuth perm="customers_view"><Customers /></RequireAuth>} />
          <Route path="/suppliers" element={<RequireAuth perm="suppliers_view"><Suppliers /></RequireAuth>} />
          <Route path="/purchases" element={<RequireAuth perm="purchases_view"><Purchases /></RequireAuth>} />
          <Route path="/purchases/:pid" element={<RequireAuth perm="purchases_view"><PurchaseDetail /></RequireAuth>} />
          <Route path="/expenses" element={<RequireAuth perm="expenses_view"><Expenses /></RequireAuth>} />
          <Route path="/reports" element={<RequireAuth ownerOnly><Reports /></RequireAuth>} />
          <Route path="/profit" element={<RequireAuth ownerOnly><ProfitReport /></RequireAuth>} />
          <Route path="/stock-report" element={<RequireAuth ownerOnly><StockReport /></RequireAuth>} />
          <Route path="/sales" element={<RequireAuth perm={['billing_view', 'reports_view']}><SalesHistory /></RequireAuth>} />
          <Route path="/daily-closing" element={<RequireAuth><DailyClosing /></RequireAuth>} />
          <Route path="/more" element={<RequireAuth><More /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth perm="settings_view"><Settings /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth perm="users_manage"><Admin /></RequireAuth>} />
          <Route path="/audit" element={<RequireAuth perm="users_manage"><Audit /></RequireAuth>} />
          <Route path="/partner" element={<RequireAuth><PartnerHome /></RequireAuth>} />
          <Route path="/management" element={<RequireAuth><ManagementDashboard /></RequireAuth>} />
          <Route path="/data-management" element={<RequireAuth><DataManagement /></RequireAuth>} />
          <Route path="/staff" element={<RequireAuth><StaffPage /></RequireAuth>} />
          <Route path="/due-reminders" element={<RequireAuth><DueReminders /></RequireAuth>} />
          <Route path="/accounts" element={<RequireAuth><AccountsBreakup /></RequireAuth>} />
          <Route path="/integrations" element={<RequireAuth><Integrations /></RequireAuth>} />
          <Route path="/system-health" element={<RequireAuth><SystemHealth /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
